'use client'

import { useMemo, useState } from 'react'
import { useLocale, useTranslations } from 'next-intl'
import { cx } from 'class-variance-authority'
import { PlusCircleIcon } from '@phosphor-icons/react/dist/csr/PlusCircle'
import { AWSImage, Select } from '@skop-app/lib-front'
import type { Product } from '@skop-app/lib-domain/types'
import { matchLabel } from '@skop-app/lib-domain/utils'

import {
  sortOrderItemsWithServicesLast,
  toOrderItem,
  useProductsGrouped,
} from '@/features/quote/hooks/useProductsGrouped'
import type { OrderModalValues } from '@/types'
import { getTranslationKey, normalizeStringValue } from '@/utils'
import { useServices } from '@/features/quote/hooks/useServices'
import { openModal } from '@/components/modals/modal.registry.events'

const ADD_NEW_SERVICE_OPTION_VALUE = '__add-new-service__'

type GroupedOption = { label: string; options: SelectOption[] }

type AddProductsProps = {
  orderValues: OrderModalValues
  setOrderValues: React.Dispatch<React.SetStateAction<OrderModalValues>>
  selectLabel?: boolean
  className?: string
}

type ProductOption = Product & {
  label: string
  value: string
  actionLabels?: string[]
}

type AddServiceOption = {
  label: string
  value: typeof ADD_NEW_SERVICE_OPTION_VALUE
  isAddServiceOption: true
  serviceName?: string
}

type SelectOption = ProductOption | AddServiceOption

const isAddServiceOption = (option: unknown): option is AddServiceOption => {
  if (!option || typeof option !== 'object') return false
  return (
    (option as { isAddServiceOption?: boolean }).isAddServiceOption === true
  )
}

export const AddProducts = ({
  orderValues,
  setOrderValues,
  className,
  selectLabel = false,
}: AddProductsProps) => {
  const locale = useLocale()
  const t = useTranslations()
  const { products, isLoading } = useProductsGrouped()
  const { services, isLoading: isLoadingServices } = useServices()
  const [serviceSearchValue, setServiceSearchValue] = useState<string>('')

  const selectedProductIds = useMemo(
    () => new Set((orderValues.items ?? []).map((item) => item.productID)),
    [orderValues.items],
  )

  const filteredProducts = useMemo<ProductOption[]>(() => {
    return products
      .filter(
        (product) =>
          product.nature !== 'service' &&
          !selectedProductIds.has(product.productID),
      )
      .map((product) => ({
        ...product,
        label: product.name || 'Nom manquant',
        value: product.productID,
      }))
  }, [products, selectedProductIds])

  const filteredServices = useMemo<ProductOption[]>(() => {
    return (services ?? []).map((product) => ({
      ...product,
      label: product.name || 'Nom manquant',
      value: product.productID,
    }))
  }, [services])

  const normalizedServiceSearchValue = normalizeStringValue(serviceSearchValue)

  const hasExactServiceMatch = useMemo(() => {
    if (!normalizedServiceSearchValue) return false

    return filteredServices.some(
      (service) =>
        normalizeStringValue(service.label) === normalizedServiceSearchValue,
    )
  }, [filteredServices, normalizedServiceSearchValue])

  const addServiceOption = useMemo<AddServiceOption>(
    () => ({
      label:
        normalizedServiceSearchValue && !hasExactServiceMatch
          ? `${t('Common.add')} "${serviceSearchValue.trim()}"`
          : t('Services-directory.add-service'),
      value: ADD_NEW_SERVICE_OPTION_VALUE,
      ...(normalizedServiceSearchValue &&
        !hasExactServiceMatch && {
          serviceName: serviceSearchValue.trim(),
        }),
      isAddServiceOption: true,
    }),
    [hasExactServiceMatch, normalizedServiceSearchValue, serviceSearchValue, t],
  )

  const groupedOptions = useMemo<GroupedOption[]>(() => {
    const groups: GroupedOption[] = []
    groups.push({
      label: t('Services-directory.group-services'),
      options: [...filteredServices, addServiceOption],
    })
    if (filteredProducts.length > 0) {
      groups.push({
        label: t('Services-directory.group-products'),
        options: filteredProducts,
      })
    }
    return groups
  }, [addServiceOption, filteredProducts, filteredServices, t])

  const totalOptionsCount = filteredProducts.length + filteredServices.length

  const addProduct = (product: ProductOption) => {
    setOrderValues((previous) => {
      const nextItems = [...(previous.items ?? []), toOrderItem(product)]

      return {
        ...previous,
        items: sortOrderItemsWithServicesLast(nextItems),
      }
    })
  }

  const addProductsFromSelection = (value: unknown) => {
    const selectedOption = value as SelectOption | null
    if (!selectedOption) return

    if (isAddServiceOption(selectedOption)) {
      setServiceSearchValue('')
      openModal('serviceUpload', { initialName: selectedOption.serviceName })
      return
    }

    setServiceSearchValue('')
    addProduct(selectedOption)
  }

  const handleInputChange = (nextValue: string, meta: { action: string }) => {
    if (meta.action === 'input-change') {
      setServiceSearchValue(nextValue)
    }

    return nextValue
  }

  return (
    <div className={cx('flex h-full w-full flex-col gap-4', className)}>
      <div className="flex flex-col gap-1.5">
        {!!selectLabel && (
          <p className="text-sm font-medium">
            {t('Services-directory.add-product-or-service')} (
            {totalOptionsCount} {t('Services-directory.available')})
          </p>
        )}
        <div className="flex w-full flex-col gap-4 lg:flex-row">
          <Select
            className="w-full"
            placeholder={
              selectLabel
                ? t('Services-directory.search-product-or-service')
                : `${t('Services-directory.add-product-or-service')} (${totalOptionsCount} ${t('Services-directory.available')})`
            }
            options={groupedOptions}
            inputValue={serviceSearchValue}
            value={null}
            isDisabled={isLoading || isLoadingServices}
            onChange={addProductsFromSelection}
            onInputChange={handleInputChange}
            components={{
              Option: (props) => {
                const option = props.data as SelectOption

                return (
                  <div ref={props.innerRef} {...props.innerProps}>
                    {isAddServiceOption(option) ? (
                      <OptionAddService label={option.label} />
                    ) : (
                      <OptionProduct
                        data={option}
                        locale={locale}
                        isService={option.nature === 'service'}
                      />
                    )}
                  </div>
                )
              },
            }}
            overflow={true}
            closeMenuOnSelect={true}
          />
        </div>
      </div>
    </div>
  )
}

type OptionAddServiceProps = {
  label: string
}

const OptionAddService = ({ label }: OptionAddServiceProps) => {
  return (
    <div className="flex h-fit w-full cursor-pointer flex-row items-center gap-1.5 p-2 text-sm font-medium text-slate-600 hover:bg-primary-100">
      <span className="ml-1.5 text-base leading-none">
        <PlusCircleIcon className="size-4 text-slate-600" />
      </span>
      <span>{label}</span>
    </div>
  )
}

type OptionProductProps = {
  data: ProductOption
  locale: string
  isService: boolean
}

const OptionProduct = (props: OptionProductProps) => {
  const { data, isService } = props
  const quantity = data.quantity?.number ?? 0
  const quantityUnit = data.quantity?.unit ?? 'unit'
  const productType = data.type || 'product'
  const type = matchLabel(productType, 'type')

  const complementKey = getTranslationKey('complement', props.locale)
  const complementByKey: Record<string, string | undefined> = {
    complement: data.complement,
    complementEN: data.complementEN,
    complementNL: data.complementNL,
  }
  const complementName = complementByKey[complementKey] ?? data.complement
  const statusText =
    data.actionLabels && data.actionLabels.length > 0
      ? data.actionLabels.join(', ')
      : (type?.label ?? productType ?? '—')

  if (isService) {
    const formattedPrice = data.price?.amount
      ? new Intl.NumberFormat(props.locale, {
          style: 'currency',
          currency: data.price?.currency ?? 'EUR',
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(data.price.amount)
      : '0,00 €'

    return (
      <div className="flex h-fit w-full cursor-pointer flex-row items-center justify-start gap-4 p-2 hover:bg-primary-100">
        <div className="ml-1.5 flex w-full flex-row items-center justify-between">
          <p className="line-clamp-1 min-w-0 flex-1 text-left text-slate-500">
            {data.label}
          </p>
          <p className="shrink-0 text-right text-sm font-medium text-slate-500">
            {formattedPrice}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-fit w-full cursor-pointer flex-row items-center justify-start gap-4 p-2 hover:bg-primary-100">
      <div className="h-fit w-fit">
        <AWSImage
          src={data?.pictures?.[0] || ''}
          alt={data?.label}
          className="size-14 object-contain object-center"
          thumbnail
        />
      </div>
      <div className="flex w-full flex-col">
        <div className="flex w-full flex-row items-center justify-between">
          <div className="min-w-0 flex-1">
            <p className="line-clamp-1 text-left text-slate-500">
              {data?.label}
            </p>
            {complementName ? (
              <p className="line-clamp-1 text-left text-xs text-slate-400">
                {complementName}
              </p>
            ) : null}
          </div>
          <div className="flex flex-row items-start justify-start gap-1 text-left text-xs text-slate-500">
            <span className="line-clamp-2 text-end text-slate-500">
              {statusText}
            </span>
          </div>
        </div>
        <div className="flex w-full flex-row items-center justify-between">
          <p className="line-clamp-1 space-x-1 text-left text-sm text-slate-300">
            <span>{quantity.toFixed(2)}</span>
            <span>{quantityUnit}</span>
          </p>
        </div>
      </div>
    </div>
  )
}
